/**
 * QuickAddSheet — dialog for adding raw macros without going through the AI pipeline.
 * Calls POST /me/logs/quick-add. Calm, ED-safe copy: no "diet", no "cheat", no "burn".
 */
import type { components } from '@nutians/api-client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api.js';
import { RECENTS_KEY } from '../hooks/useRecents.js';

type DaySummary = components['schemas']['DaySummary'];

interface QuickAddSheetProps {
  open: boolean;
  date: string;
  onClose: () => void;
  onDayUpdate: (day: DaySummary) => void;
}

export function QuickAddSheet({ open, date, onClose, onDayUpdate }: QuickAddSheetProps) {
  const { t } = useTranslation('log');
  const qc = useQueryClient();
  const [label, setLabel] = useState('');
  const [kcal, setKcal] = useState('');
  const [proteinG, setProteinG] = useState('');
  const [carbsG, setCarbsG] = useState('');
  const [fatG, setFatG] = useState('');
  const [fiberG, setFiberG] = useState('');
  const [warning, setWarning] = useState<string | null>(null);
  const kcalRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setLabel('');
      setKcal('');
      setProteinG('');
      setCarbsG('');
      setFatG('');
      setFiberG('');
      setWarning(null);
      setTimeout(() => kcalRef.current?.focus(), 50);
    }
  }, [open]);

  const mutation = useMutation({
    mutationFn: async () => {
      const kcalNum = Number(kcal);
      if (Number.isNaN(kcalNum) || kcalNum < 0) throw new Error('Invalid kcal');

      const { data, error } = await api.POST('/me/logs/quick-add', {
        body: {
          date,
          label: label.trim() || undefined,
          kcal: kcalNum,
          proteinG: proteinG ? Number(proteinG) : undefined,
          carbsG: carbsG ? Number(carbsG) : undefined,
          fatG: fatG ? Number(fatG) : undefined,
          fiberG: fiberG ? Number(fiberG) : undefined,
        },
      });
      if (error || !data) throw new Error('Failed to quick-add');
      return data;
    },
    onSuccess: (data) => {
      onDayUpdate(data.day);
      setWarning(data.warning ?? null);
      void qc.invalidateQueries({ queryKey: RECENTS_KEY });
      if (!data.warning) onClose();
    },
  });

  if (!open) return null;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    mutation.mutate();
  }

  const numField = (
    labelText: string,
    value: string,
    setter: (v: string) => void,
    required = false,
    ref?: React.RefObject<HTMLInputElement | null>,
  ) => {
    const inputId = `qa-${labelText.replace(/\s+/g, '-').toLowerCase()}`;
    return (
      <div className="flex flex-col gap-1">
        <label htmlFor={inputId} className="text-xs font-medium text-ink dark:text-ink">
          {labelText}
          {required && <span className="text-clay ml-1">*</span>}
        </label>
        <input
          id={inputId}
          ref={ref}
          type="number"
          min="0"
          step="any"
          value={value}
          onChange={(e) => setter(e.target.value)}
          required={required}
          className="
            rounded-lg border border-ink/20 px-3 py-2 text-sm text-ink
            focus:outline-none focus:ring-2 focus:ring-leaf
            dark:border-ink/20 dark:bg-shell dark:text-ink
          "
        />
      </div>
    );
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-4 sm:items-center"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-label={t('quickLog.quickAddTitle')}
    >
      <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl dark:bg-shell">
        <h2 className="mb-4 text-base font-semibold text-ink dark:text-ink">
          {t('quickLog.quickAddTitle')}
        </h2>

        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <label htmlFor="qa-label" className="text-xs font-medium text-ink dark:text-ink">
              {t('quickLog.quickAddLabel')}
            </label>
            <input
              id="qa-label"
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder={t('quickLog.quickAddLabelPlaceholder')}
              maxLength={200}
              className="
                rounded-lg border border-ink/20 px-3 py-2 text-sm text-ink placeholder-neutral-400
                focus:outline-none focus:ring-2 focus:ring-leaf
                dark:border-ink/20 dark:bg-shell dark:text-ink dark:placeholder-neutral-500
              "
            />
          </div>

          {numField(t('quickLog.quickAddKcal'), kcal, setKcal, true, kcalRef)}
          {numField(t('quickLog.quickAddProtein'), proteinG, setProteinG)}
          {numField(t('quickLog.quickAddCarbs'), carbsG, setCarbsG)}
          {numField(t('quickLog.quickAddFat'), fatG, setFatG)}
          {numField(t('quickLog.quickAddFiber'), fiberG, setFiberG)}

          {warning && (
            <p className="text-xs text-acorn dark:text-acorn">{t('quickLog.quickAddWarning')}</p>
          )}

          {mutation.isError && (
            <p className="text-xs text-clay dark:text-clay" role="alert">
              {t('error')}
            </p>
          )}

          <div className="flex gap-2 pt-1">
            <button
              type="submit"
              disabled={!kcal || mutation.isPending}
              className="
                flex-1 rounded-xl bg-leaf px-4 py-2.5 text-sm font-semibold text-white
                hover:bg-leaf-deep focus:outline-none focus:ring-2 focus:ring-leaf
                disabled:opacity-40 disabled:cursor-not-allowed
              "
            >
              {mutation.isPending ? t('loading') : t('quickLog.quickAddButton')}
            </button>
            {warning ? (
              <button
                type="button"
                onClick={onClose}
                className="rounded-xl border border-husk px-4 py-2.5 text-sm font-medium text-ink hover:bg-husk/30 dark:border-ink/20 dark:text-ink"
              >
                Close
              </button>
            ) : (
              <button
                type="button"
                onClick={onClose}
                className="rounded-xl border border-husk px-4 py-2.5 text-sm font-medium text-ink hover:bg-husk/30 dark:border-ink/20 dark:text-ink"
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
